import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useAuth } from "../../contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { Mail, ArrowLeft, CheckCircle } from "lucide-react";

const ForgotPassword = () => {
  const [emailSent, setEmailSent] = useState(false);
  const { forgotPassword } = useAuth();
  const { t } = useTranslation();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
  } = useForm();

  const onSubmit = async (data) => {
    const result = await forgotPassword(data.email);
    if (result.success) {
      setEmailSent(true);
    } else {
      setError("root", { message: result.error });
    }
  };

  const renderContent = () => {
    if (emailSent) {
      return (
        <div className="space-y-8">
          <div className="text-center space-y-3">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-300">
              <CheckCircle className="h-8 w-8" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.4em] text-cyan-200/70">
                {t("auth.forgotPassword.checkInbox")}
              </p>
              <h2 className="mt-2 text-3xl font-semibold text-white">
                {t("auth.forgotPassword.checkInbox")}
              </h2>
              <p className="mt-2 text-sm text-slate-400">
                {t("auth.forgotPassword.successMessage")}
              </p>
            </div>
          </div>

          <div className="rounded-2xl border border-cyan-500/20 bg-cyan-500/10 p-4 text-sm text-cyan-100">
            If it is not in your inbox after a minute, check spam or your
            security filters. The link expires in one hour.
          </div>

          <div className="text-center">
            <Link
              to="/login"
              className="inline-flex items-center justify-center gap-2 text-sm font-medium text-cyan-200 hover:text-white"
            >
              <ArrowLeft className="h-4 w-4" />
              {t("auth.forgotPassword.backToLogin")}
            </Link>
          </div>
        </div>
      );
    }

    return (
      <>
        <div className="text-center space-y-2">
          <p className="text-xs uppercase tracking-[0.4em] text-cyan-200/70">
            {t("auth.forgotPassword.title")}
          </p>
          <h2 className="text-3xl font-semibold text-white">
            {t("auth.forgotPassword.title")}
          </h2>
          <p className="text-sm text-slate-400">
            {t("auth.forgotPassword.subtitle")}
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
          <div>
            <label className="mb-2 block text-sm font-medium text-slate-200">
              {t("auth.forgotPassword.email")}
            </label>
            <div className="relative">
              <input
                {...register("email", {
                  required: "Email is required",
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: "Invalid email address",
                  },
                })}
                type="email"
                className="w-full rounded-2xl border border-white/10 bg-slate-950/40 px-12 py-3 text-white placeholder:text-slate-500 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-cyan-400"
                placeholder="you@company.ch"
              />
              <Mail className="input-icon-left text-slate-400" />
            </div>
            {errors.email && (
              <p className="mt-1 text-sm text-red-400">
                {errors.email.message}
              </p>
            )}
          </div>

          {errors.root && (
            <div className="rounded-2xl border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-200">
              {errors.root.message}
            </div>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            className="inline-flex w-full items-center justify-center rounded-2xl bg-gradient-to-r from-cyan-400 via-blue-500 to-indigo-500 px-6 py-3 text-base font-semibold text-white shadow-lg shadow-cyan-500/30 transition hover:shadow-cyan-400/50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isSubmitting ? (
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
            ) : (
              t("auth.forgotPassword.submit")
            )}
          </button>

          <div className="text-center text-sm text-slate-400">
            <Link
              to="/login"
              className="inline-flex items-center justify-center gap-2 font-medium text-cyan-200 hover:text-white"
            >
              <ArrowLeft className="h-4 w-4" />
              {t("auth.forgotPassword.backToLogin")}
            </Link>
          </div>
        </form>
      </>
    );
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#020617] text-white">
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950" />
        <div className="absolute -top-32 right-[-10%] h-96 w-96 rounded-full bg-cyan-500/30 blur-[120px]" />
        <div className="absolute bottom-0 left-0 h-72 w-72 rounded-full bg-blue-500/20 blur-[120px]" />
        <div className="absolute inset-0 opacity-20">
          <div className="[background:radial-gradient(circle_at_1px,_rgba(255,255,255,0.25)_1px,_transparent_0)] h-full w-full [background-size:40px_40px]" />
        </div>
      </div>

      <div className="relative z-10 flex min-h-screen items-center justify-center px-4">
        <div className="w-full max-w-xl rounded-3xl border border-white/10 bg-slate-900/70 p-8 shadow-2xl shadow-cyan-500/20 backdrop-blur-xl">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
